/**
 * Video popups for `.play-video` triggers (partials/video.php, used by the video, inside,
 * gaming-guide-page-title and video-slider sections).
 *
 * Replaces the previous hand-rolled #cover / #video_block / #iframe_wrapper / #button_close
 * overlay in video.js. The presentation is unchanged - modal--media reproduces those styles -
 * but focus trapping, Esc and scroll locking now come from the shared engine.
 *
 * One deliberate difference: the old code positioned the player at `scrollTop + 60`; the dialog
 * centres it in the viewport instead. Both put it in view; centring is the saner behaviour.
 *
 * Both original content paths are preserved:
 *   data-src="<url>"  ->  YouTube iframe
 *   data-src="#"      ->  self-hosted <video> assembled from `.wp_video .source` elements
 */

interface EsrbModalGlobal {
	isSupported(): boolean;
	open( target: string | HTMLDialogElement, trigger?: HTMLElement | null ): boolean;
	close( dialog?: HTMLDialogElement | null ): void;
	create( content: Node, variant?: string ): HTMLDialogElement;
	closeButton(): HTMLButtonElement;
}

(function () {
	'use strict';

	function engine(): EsrbModalGlobal | null {
		var api = ( window as unknown as { EsrbModal?: EsrbModalGlobal } ).EsrbModal;

		return api && api.isSupported() ? api : null;
	}

	/** Self-hosted player, assembled from the markup the old implementation expected. */
	function selfHostedVideo(): HTMLVideoElement {
		var holder = document.querySelector( '.wp_video' );
		var video = document.createElement( 'video' );

		video.controls = true;
		video.autoplay = true;
		video.loop = true;

		var poster = holder ? holder.getAttribute( 'data-poster' ) : null;

		if ( poster ) {
			video.poster = poster;
		}

		if ( holder ) {
			Array.prototype.forEach.call(
				holder.querySelectorAll( '.source' ),
				function ( node: Element ) {
					var source = document.createElement( 'source' );

					source.src = node.getAttribute( 'data-src' ) || '';
					source.type = node.getAttribute( 'data-type' ) || '';
					video.appendChild( source );
				}
			);
		}

		return video;
	}

	function youtubeFrame( src: string ): HTMLIFrameElement {
		var frame = document.createElement( 'iframe' );

		// Same query string the previous implementation used.
		frame.src = src + '?rel=0&autoplay=1&enablejsapi=1';
		frame.setAttribute( 'frameborder', '0' );
		frame.setAttribute( 'allowfullscreen', 'true' );
		frame.setAttribute( 'allow', 'autoplay; encrypted-media' );

		return frame;
	}

	function mediaFor( src: string ): HTMLElement {
		var wrapper = document.createElement( 'div' );

		wrapper.className = 'modal__media';
		wrapper.appendChild( '#' === src ? selfHostedVideo() : youtubeFrame( src ) );

		return wrapper;
	}

	// Delegated: video sections can sit inside sliders that clone their slides.
	document.addEventListener( 'click', function ( event: MouseEvent ) {
		var origin = event.target;

		if ( !( origin instanceof Element ) ) {
			return;
		}

		var trigger = origin.closest<HTMLElement>( '.play-video' );

		if ( !trigger ) {
			return;
		}

		var api = engine();

		if ( !api ) {
			return;
		}

		var src = trigger.getAttribute( 'data-src' );

		if ( !src ) {
			return;
		}

		event.preventDefault();

		var content = document.createDocumentFragment();

		content.appendChild( mediaFor( src ) );
		content.appendChild( api.closeButton() );

		var dialog = api.create( content, 'modal--media' );

		// Kept for parity with the previous implementation, which toggled this class.
		// Captured so the narrowing survives into the callback.
		var playing = trigger;

		playing.classList.add( 'playing' );
		dialog.addEventListener( 'close', function () {
			playing.classList.remove( 'playing' );
		} );

		api.open( dialog, playing );
	} );
})();
